"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MarketId, Timeframe } from "@/stores/tradeStore";
import { getCoinForMarket, type CandleData } from "@/lib/hyperliquid";

const WS_URL = "wss://api.hyperliquid.xyz/ws";

const PING_INTERVAL = 30_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

// Distinct dexes we need allMids subscriptions for
const ALL_DEXES: (string | undefined)[] = [undefined, "cash", "xyz"];

interface UseHyperliquidSocketOptions {
  market: MarketId;
  timeframe: Timeframe;
  onCandle?: (candle: CandleData) => void;
  onMidPrices?: (prices: Record<string, string>, dex?: string) => void;
  enabled?: boolean;
}

interface UseHyperliquidSocketReturn {
  isConnected: boolean;
  reconnect: () => void;
  /** FEAT-003: market-mapping predicate — true iff `market` has a
   * `MARKET_CONFIG` Hyperliquid coin mapping. Says nothing about live
   * connection state (see `isConnected` for that); an unmapped market
   * never attempts a candle subscription regardless of connection state. */
  hasLiveFeed: boolean;
}

const hlIntervalToTimeframe = (interval: string): Timeframe | null => {
  const map: Record<string, Timeframe> = {
    "1m": "1m",
    "5m": "5m",
    "15m": "15m",
    "1h": "1h",
    "4h": "4h",
    "1d": "1d",
    "1w": "1w",
    "1M": "1M",
  };
  return map[interval] ?? null;
};

export function useHyperliquidSocket(
  options: UseHyperliquidSocketOptions
): UseHyperliquidSocketReturn {
  const { market, timeframe, onCandle, onMidPrices, enabled = true } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);
  const connectRef = useRef<(() => void) | undefined>(undefined);

  // Callback refs to avoid stale closures
  const onCandleRef = useRef(onCandle);
  const onMidPricesRef = useRef(onMidPrices);
  useEffect(() => {
    onCandleRef.current = onCandle;
  }, [onCandle]);
  useEffect(() => {
    onMidPricesRef.current = onMidPrices;
  }, [onMidPrices]);

  // Track current subscription so we can unsub/resub on change
  const currentSubRef = useRef<{ coin: string; interval: string } | null>(null);
  const marketRef = useRef(market);
  const timeframeRef = useRef(timeframe);
  useEffect(() => {
    marketRef.current = market;
  }, [market]);
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  // Helper: send JSON over the open socket
  const sendJson = useCallback((msg: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const subscribeCandleSub = useCallback(
    (coin: string, interval: string) => {
      sendJson({ method: "subscribe", subscription: { type: "candle", coin, interval } });
      currentSubRef.current = { coin, interval };
    },
    [sendJson]
  );

  const unsubscribeCandleSub = useCallback(() => {
    if (currentSubRef.current) {
      sendJson({
        method: "unsubscribe",
        subscription: {
          type: "candle",
          coin: currentSubRef.current.coin,
          interval: currentSubRef.current.interval,
        },
      });
      currentSubRef.current = null;
    }
  }, [sendJson]);

  // Connection lifecycle — stored in a ref so onclose can self-reference
  // without creating a useCallback dependency cycle.
  useEffect(() => {
    connectRef.current = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      intentionalCloseRef.current = false;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptRef.current = 0;

        // Subscribe candle for current market + timeframe, if the market
        // has a Hyperliquid coin mapping (FEAT-003: unmapped symbols get
        // no live candle feed instead of a throw).
        const coin = getCoinForMarket(marketRef.current);
        const interval: string = timeframeRef.current;
        if (coin) {
          ws.send(
            JSON.stringify({
              method: "subscribe",
              subscription: { type: "candle", coin, interval },
            })
          );
          currentSubRef.current = { coin, interval };
        }

        // Subscribe allMids for every dex
        for (const dex of ALL_DEXES) {
          const sub: Record<string, unknown> = { type: "allMids" };
          if (dex) sub.dex = dex;
          ws.send(JSON.stringify({ method: "subscribe", subscription: sub }));
        }

        // Keepalive ping
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ method: "ping" }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.channel === "candle") {
            const d = msg.data;
            if (!d) return;
            const tf = hlIntervalToTimeframe(d.i);
            if (!tf || tf !== timeframeRef.current) return;
            // Reject stale candles from a previous market subscription, and
            // any candle for a market with no active subscription (FEAT-003:
            // unmapped markets never subscribe, so getCoinForMarket is null).
            const currentCoin = getCoinForMarket(marketRef.current);
            if (!currentCoin || d.s !== currentCoin) return;

            const candle: CandleData = {
              time: Math.floor(d.t / 1000),
              open: parseFloat(d.o),
              high: parseFloat(d.h),
              low: parseFloat(d.l),
              close: parseFloat(d.c),
              volume: parseFloat(d.v),
            };
            onCandleRef.current?.(candle);
          } else if (msg.channel === "allMids") {
            const mids = msg.data?.mids;
            if (mids) {
              onMidPricesRef.current?.(mids, msg.data?.dex);
            }
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (pingRef.current) {
          clearInterval(pingRef.current);
          pingRef.current = null;
        }
        currentSubRef.current = null;

        if (
          !intentionalCloseRef.current &&
          reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(
            RECONNECT_BASE_MS * 2 ** reconnectAttemptRef.current,
            RECONNECT_MAX_MS
          );
          reconnectAttemptRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => connectRef.current?.(), delay);
        }
      };

      ws.onerror = () => {
        // onclose fires after this — reconnection handled there
      };
    };
  });

  // Initial connection + cleanup
  useEffect(() => {
    if (!enabled) return;
    connectRef.current?.();

    return () => {
      intentionalCloseRef.current = true;
      if (pingRef.current) clearInterval(pingRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [enabled]);

  // Handle market/timeframe changes — unsub old, sub new
  useEffect(() => {
    if (!isConnected) return;

    const newCoin = getCoinForMarket(market);
    const newInterval: string = timeframe;

    const current = currentSubRef.current;

    if (!newCoin) {
      // FEAT-003: market has no Hyperliquid coin mapping — drop any
      // existing candle subscription and skip resubscribing.
      if (current) unsubscribeCandleSub();
      return;
    }

    if (current?.coin === newCoin && current?.interval === newInterval) return;

    unsubscribeCandleSub();
    subscribeCandleSub(newCoin, newInterval);
  }, [market, timeframe, isConnected, unsubscribeCandleSub, subscribeCandleSub]);

  const hasLiveFeed = useMemo(() => getCoinForMarket(market) !== null, [market]);

  const reconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    wsRef.current?.close();
    reconnectAttemptRef.current = 0;
    setTimeout(() => connectRef.current?.(), 100);
  }, []);

  return { isConnected, reconnect, hasLiveFeed };
}
